import { useState, useEffect, memo } from "react";

import {
  useMobileVerificationContext,
  useSignUpContext,
} from "@zenith/context";
import { AppNavigation, AppStepper } from "@zenith/hooks";
import { PinInputComponent } from "@zenith/components";
import {
  ApiResponseDTO,
  ApiResponseDTOEncryptionDTO,
  CreateUserOnboardingRequestDTO,
  OnboardingApiService,
  UtilityApiService,
} from "@zenith/services/user";
import { DEFAULT_CHANNEL, PIN_LENGTH } from "@zenith/data/constants";
import {
  setConfirmPinValidations,
  setPinValidations,
} from "@zenith/features/Authentication/validations/AuthValidations";
import { SIGN_UP } from "@zenith/routes/paths";
import { encrypt } from "@zenith/utils";

/* eslint-disable-next-line */
export interface SignUpLoginPinPageProps {}

const SignUpLoginPinPage = memo(function SignUpLoginPinPage(
  props: SignUpLoginPinPageProps
) {
  const xApiKey = import.meta.env.VITE_X_API_KEY;

  const { navigateTo } = AppNavigation();
  const {
    mobileVerificationContext: { countryCode, phoneNumber },
  } = useMobileVerificationContext();

  const { setSignUpContext } = useSignUpContext();

  useEffect(() => {
    if (!countryCode && !phoneNumber) {
      navigateTo(SIGN_UP);
    }
  });

  const { nextStep } = AppStepper();

  const [formData, setFormData] = useState<{
    pin: string;
    confirmPin: string;
  }>({
    pin: "",
    confirmPin: "",
  });

  const [newPin, setNewPin] = useState<Array<number | string>>(
    new Array(PIN_LENGTH).fill("")
  );
  const [newConfirmPin, setConfirmNewPin] = useState<Array<number | string>>(
    new Array(PIN_LENGTH).fill("")
  );

  const [pinError, setPinError] = useState<{
    hasError: boolean;
    message: string;
  }>();

  const [confirmPinError, setConfirmPinError] = useState<{
    hasError: boolean;
    message: string;
  }>({
    hasError: false,
    message: "",
  });

  const [disableContinueButton, setDisableContinueButton] = useState(false);

  const onPinChanged = (pinEntry: number | string, index: number) => {
    const pin = [...newPin];
    pin[index] = pinEntry;
    setNewPin(pin);
    setPinError({ message: "", hasError: false });
    setDisableContinueButton(false);
    setFormData({ ...formData, pin: pin.join("") });
  };

  const onPinBlur = (): void => {
    setPinError(setPinValidations(formData.pin));
  };

  const onConfirmPinChanged = (pinEntry: number | string, index: number) => {
    const confirmPin = [...newConfirmPin];
    confirmPin[index] = pinEntry;
    setConfirmNewPin(confirmPin);
    setConfirmPinError({ message: "", hasError: false });
    setDisableContinueButton(false);
    setFormData({ ...formData, confirmPin: confirmPin.join("") });
  };

  const onConfirmPinBlur = (): void => {
    setConfirmPinError(setConfirmPinValidations(formData.confirmPin));
  };

  //#region Aux Methods

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (formData.pin !== formData.confirmPin) {
      setDisableContinueButton(true);
      setConfirmPinError({
        hasError: true,
        message:
          "PIN code doesn´t match. Please make sure to enter the same PIN in both fields",
      });
      return;
    }

    UtilityApiService.getPublicKey(DEFAULT_CHANNEL, xApiKey)
      .then((response: ApiResponseDTOEncryptionDTO) => {
        if (!response.data) return;
        if (response.success) {
          const publicKeyBase64 = response.data.publicKeyBase64;

          const encryptedPin = encrypt(formData.pin, publicKeyBase64 || "");
          const confirmEncryptedPin = encrypt(
            formData.confirmPin,
            publicKeyBase64 || ""
          );

          if (
            publicKeyBase64 &&
            publicKeyBase64.length > 0 &&
            encryptedPin &&
            encryptedPin.length > 0 &&
            confirmEncryptedPin &&
            confirmEncryptedPin.length > 0
          ) {
            validatePin(encryptedPin, confirmEncryptedPin);
          }
        }
      })
      .catch((error) => {
        throw new Error(error);
      });
  };

  function validatePin(
    encryptedPin: string,
    confirmEncryptedPin: string
  ): void {
    const request: CreateUserOnboardingRequestDTO = {
      confirmPasscode: confirmEncryptedPin,
      countryCode,
      passcode: encryptedPin,
      phoneNumber,
    };

    setSignUpContext({
      countryCode: countryCode,
      encryptedPin: encryptedPin,
      phoneNumber: phoneNumber,
    });

    OnboardingApiService.createNewUserOnboarding(request)
      .then((response: ApiResponseDTO) => {
        nextStep();
      })
      .catch((error) => {
        if (error.body.internalCode === "ZENZ-CROSS-008") {
          setConfirmPinError({
            hasError: true,
            message: "User was not found by phone number",
          });
        }
      });
  }

  //#endregion Aux Methods

  return (
    <form onSubmit={handleSubmit}>
      <div>
        <h5 className="mb-4 text-4xl font-bold">Login PIN</h5>
        <span className="text-neutral-600">
          This will be used to login into the application
        </span>
      </div>
      <div className="mt-12">
        <div className="mb-10 flex w-full flex-row space-x-1">
          <PinInputComponent
            id="create-new-pin"
            label="Create six (6) digit PIN"
            labelClassName="mb-1 text-neutral-600"
            setFocus={true}
            pinLength={PIN_LENGTH}
            pinContainerClass="gap-2 md:gap-4 xl:gap-6"
            pin={newPin}
            onPinChanged={onPinChanged}
            onBlur={onPinBlur}
            type="tel"
            masked={true}
            hasError={pinError?.hasError}
            isValidating={false}
            validationMessage={pinError?.message}
          />
        </div>

        <div className="mt-12 flex w-full flex-row space-x-1">
          <PinInputComponent
            id="create-new-confirm-pin"
            label="Confirm six (6) digit PIN"
            labelClassName="mb-1 text-neutral-600"
            pinLength={PIN_LENGTH}
            pin={newConfirmPin}
            onPinChanged={onConfirmPinChanged}
            pinContainerClass="gap-2 md:gap-4 xl:gap-6"
            onBlur={onConfirmPinBlur}
            type="tel"
            hasError={confirmPinError?.hasError}
            isValidating={false}
            validationMessage={confirmPinError?.message}
          />
        </div>
      </div>

      <div className="stepper-buttons-container">
        <button
          id="back-button"
          className="secondary-button"
          onClick={() => navigateTo(SIGN_UP)}
          type="button"
        >
          Back
        </button>

        <button
          id="continue-button"
          className="primary-button"
          type="submit"
          disabled={
            formData.pin.length < PIN_LENGTH ||
            formData.confirmPin.length < PIN_LENGTH ||
            disableContinueButton
          }
        >
          Continue
        </button>
      </div>
    </form>
  );
});

export default SignUpLoginPinPage;
